import { useMemo } from 'react';

import {
  type CustomSkillTreeNode,
  useCustomSkillTaxonomyTree,
} from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useSelectionListsForKeys } from '../../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { standardGqlSelectionListKeys } from '../../../../../shared/selection-lists/types';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { nestSelectionLists } from '../../../filters/selectionList/nestSelectionLists';
import { useRoleSelectionLists } from '../../../filters/selectionList/useRoleSelectionList';
import {
  type Filter,
  FilterId,
  FilterType,
  type TreeFilter,
} from '../../../filters/types/filters';
import type { CompositionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { SubfilterTree } from '../../PageState/types';
import { useFilteredSelectionTree } from './useFilteredSelectionTree';

const EMPTY_SUBFILTER_LIST: SubfilterTree[] = [];

type ShareOfSegmentSubfiltersState = {
  subfilterList: SubfilterTree[];
  isTreeSubfilter: boolean;
  defaultSelection: SubfilterTree | null;
};

export const useShareOfSegmentSubfilters = (
  secondaryDimension: CompositionSecondaryDimensionValues
): ShareOfSegmentSubfiltersState => {
  const effectiveFilters = useEffectiveFilters();
  const { selectionLists: gqlSelectionLists } = useSelectionListsForKeys(
    standardGqlSelectionListKeys
  );
  const roleSelectionLists = useRoleSelectionLists();
  const geographyMode = useGeographyMode();

  const selectionLists =
    secondaryDimension === 'role' ? roleSelectionLists : gqlSelectionLists;

  const filteredSelectionLists = useFilteredSelectionTree({
    secondaryDimension,
    effectiveFilters,
    selectionLists,
    geographyMode,
  });

  const { customSkillTree } = useCustomSkillTaxonomyTree();

  const treeData = useMemo(() => {
    if (!filteredSelectionLists.length) return null;

    return nestSelectionLists(filteredSelectionLists);
  }, [filteredSelectionLists]);
  const customSkillSubfilterList = useCustomSkillSubfilters({
    secondaryDimension,
    customSkillTree,
    effectiveFilters,
  });

  const subfilterList =
    customSkillSubfilterList && customSkillSubfilterList.length > 0
      ? customSkillSubfilterList
      : (treeData ?? EMPTY_SUBFILTER_LIST);

  const isTreeSubfilter = subfilterList.some(
    (item) => item?.children && item.children.length > 0
  );

  const defaultSelection = useMemo(
    () => getDefaultSelection(subfilterList),
    [subfilterList]
  );

  return {
    subfilterList,
    isTreeSubfilter,
    defaultSelection,
  };
};

type CustomSkillSubfilterArgs = {
  secondaryDimension: CompositionSecondaryDimensionValues;
  customSkillTree: CustomSkillTreeNode[] | null;
  effectiveFilters: Filter[];
};

const useCustomSkillSubfilters = ({
  secondaryDimension,
  customSkillTree,
  effectiveFilters,
}: CustomSkillSubfilterArgs): SubfilterTree[] | null => {
  return useMemo(() => {
    if (secondaryDimension !== 'skills') return null;

    if (!customSkillTree || customSkillTree.length === 0) return null;

    const customSkillFilter = effectiveFilters.find(
      (filter): filter is TreeFilter =>
        filter.id === FilterId.CUSTOM_SKILL && filter.type === FilterType.TREE
    );

    if (
      !customSkillFilter ||
      !Array.isArray(customSkillFilter.selections) ||
      customSkillFilter.selections.length === 0
    ) {
      return customSkillTree.map((skill0) => ({
        id: skill0.id,
        name: skill0.name,
      }));
    }

    const findNodeById = (id: string) => {
      const stack = [...customSkillTree];
      while (stack.length) {
        const node = stack.pop();
        if (!node) continue;
        if (node.id === id) return node;
        if (node.children && node.children.length) {
          stack.push(...node.children);
        }
      }
      return null;
    };

    const mapNodeWithChildren = (node: CustomSkillTreeNode): SubfilterTree => ({
      id: node.id,
      name: node.name,
      ...(Array.isArray(node.children) && node.children.length > 0
        ? {
            children: (node.children as CustomSkillTreeNode[]).map((child) => ({
              id: child.id,
              name: child.name,
            })),
          }
        : {}),
    });

    const levelOrder = {
      skill_0: 0,
      skill_1: 1,
      skill_2: 2,
      skill_3: 3,
      skill_4: 4,
    } as const;

    const deepestLevel = customSkillFilter.selections.reduce<
      0 | 1 | 2 | 3 | 4 | null
    >((acc, selection) => {
      const level = (
        levelOrder as Record<string, 0 | 1 | 2 | 3 | 4 | undefined>
      )[selection.levelId as string];
      if (level === undefined) return acc;
      if (acc === null) return level;
      return level > acc ? level : acc;
    }, null);

    if (deepestLevel === null) return null;

    const deepestSelections = customSkillFilter.selections.filter(
      (selection) => {
        const level = (levelOrder as Record<string, number | undefined>)[
          selection.levelId as string
        ];
        return level === deepestLevel;
      }
    );

    if (deepestLevel === 0) {
      const groups: SubfilterTree[] = [];
      deepestSelections.forEach((selection) => {
        const root = findNodeById(selection.id);
        if (!root || !Array.isArray(root.children)) return;
        const children = root.children.map((child) => ({
          id: child.id,
          name: child.name,
        }));
        groups.push({ id: root.id, name: root.name, children });
      });
      return groups;
    }

    if (deepestLevel >= 1 && deepestLevel <= 3) {
      return deepestSelections
        .map((selection) => {
          const node = findNodeById(selection.id);
          return node ? mapNodeWithChildren(node) : null;
        })
        .filter((node): node is SubfilterTree => node !== null);
    }

    return deepestSelections
      .map((selection) => {
        const node = findNodeById(selection.id);
        return node ? { id: node.id, name: node.name } : null;
      })
      .filter((node): node is SubfilterTree => node !== null);
  }, [secondaryDimension, customSkillTree, effectiveFilters]);
};

const isNonOtherEntity = (name?: string) =>
  !!name && name !== 'Miscellaneous' && name !== 'Other';

const getFirstValidChild = (
  children?: SubfilterTree[]
): SubfilterTree | null => {
  if (!children) return null;
  const validChild = children.find((child) => {
    if (!child?.id || !child?.name) return false;
    return isNonOtherEntity(child.name);
  });
  return validChild ? { id: validChild.id, name: validChild.name } : null;
};

const getDefaultSelection = (
  subfilterList: SubfilterTree[]
): SubfilterTree | null => {
  for (const item of subfilterList) {
    // when there's a global filter applied for the subfilter
    const firstNestedChild = getFirstValidChild(item.children);
    if (firstNestedChild) {
      return firstNestedChild;
    }

    const isSubFilterTreeWithoutGlobalFilter =
      item.id && item.name && !item.children?.length;
    if (isSubFilterTreeWithoutGlobalFilter && isNonOtherEntity(item.name)) {
      return { id: item.id, name: item.name };
    }
  }
  return null;
};
